import React, { useState, useCallback, useMemo } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const StripePayment = React.memo(({ onSuccess, onError, customerEmail, billingDetails, amount, isProcessing: parentIsProcessing }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardError, setCardError] = useState('');

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    setIsProcessing(true);
    setCardError('');

    if (!stripe || !elements) {
      console.error('Stripe or Elements not initialized');
      setCardError('Payment system not initialized. Please refresh the page.');
      setIsProcessing(false);
      return;
    }

    try {
      console.log('Creating payment intent...');
      const billingAddress = {
        line1: billingDetails.address?.line1 || billingDetails.address || '',
        ...(billingDetails.state && { state: String(billingDetails.state) }),
        ...(billingDetails.city && { city: String(billingDetails.city) }),
        ...(billingDetails.zipCode && { postal_code: String(billingDetails.zipCode) }),
        country: String(billingDetails.country || 'LV')
      };

      console.log('Billing address format:', JSON.stringify(billingAddress));

      const response = await fetch(`${import.meta.env.VITE_API_URL}/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amount,
          email: customerEmail,
          billingDetails: {
            name: `${billingDetails.firstName || ''} ${billingDetails.lastName || ''}`.trim() || 'Customer',
            email: billingDetails.email,
            phone: billingDetails.phone,
            address: billingAddress
          }
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Payment intent creation failed:', errorData);
        throw new Error(errorData.error || 'Failed to create payment intent');
      }

      const data = await response.json();
      if (!data.clientSecret) {
        console.error('No client secret in response:', data);
        throw new Error('Invalid payment intent response');
      }

      console.log('Payment intent created successfully');

      const paymentMethodData = {
        billing_details: {
          name: `${billingDetails.firstName || ''} ${billingDetails.lastName || ''}`.trim() || 'Customer',
          email: billingDetails.email,
          phone: billingDetails.phone,
          address: billingAddress
        }
      };

      console.log('Confirming card payment...');
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        data.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            ...paymentMethodData
          }
        }
      );

      if (stripeError) {
        console.error('Stripe error:', stripeError);
        throw new Error(stripeError.message);
      }

      if (!paymentIntent) {
        console.error('No payment intent returned from confirmation');
        throw new Error('Payment confirmation failed');
      }

      console.log('Payment confirmed:', paymentIntent);
      
      if (onSuccess) {
        onSuccess({ 
          paymentIntent,
          orderDetails: {
            id: paymentIntent.id,
            amount: paymentIntent.amount,
            status: paymentIntent.status,
            created: new Date(paymentIntent.created * 1000).toLocaleString('lv-LV'),
            email: customerEmail,
            billingDetails: {
              name: `${billingDetails.firstName || ''} ${billingDetails.lastName || ''}`.trim() || 'Customer',
              email: billingDetails.email,
              phone: billingDetails.phone,
              address: {
                line1: billingDetails.address,
                city: billingDetails.city,
                postal_code: billingDetails.zipCode,
                country: billingDetails.country
              }
            }
          }
        });
      }
    } catch (err) {
      console.error('Payment error:', err);
      setCardError(err.message);
      if (onError) {
        onError(err.message);
      }
    } finally {
      setIsProcessing(false);
    }
  }, [stripe, elements, customerEmail, billingDetails, amount, onSuccess, onError]);

  const cardElementOptions = useMemo(() => ({
    style: {
      base: {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: '"Inter", system-ui, sans-serif',
        fontSmoothing: 'antialiased',
        '::placeholder': {
          color: '#aab7c4',
        },
        backgroundColor: 'transparent',
      },
      invalid: {
        color: '#ef4444',
        iconColor: '#ef4444',
      },
    },
    hidePostalCode: true,
  }), []);

  return (
    <div className="mt-4">
      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <label className="block text-white mb-2 font-medium">Kartes informācija</label>
          <div className="p-4 bg-gray-800 border border-gray-600 rounded-md shadow-sm">
            <CardElement options={cardElementOptions} />
          </div>
          {cardError && (
            <div className="text-red-500 text-sm mt-2">{cardError}</div>
          )}
        </div>
        
        <button
          type="submit"
          disabled={!stripe || isProcessing || parentIsProcessing}
          className="w-full bg-green-400 hover:bg-green-500 text-black font-semibold py-3 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isProcessing || parentIsProcessing ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Apstrādājam...
            </>
          ) : (
            <>
              <span>Apmaksāt pasūtījumu</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
});

StripePayment.displayName = 'StripePayment';

export default StripePayment; 